import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";
import ErrorBox from "../../components/ErrorBox";
import CustomButton from "../../components/CustomButton";
import addIcon from "../../assets/add_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import axiosClient from "../../services/axiosClient";
import AreYouSureModal from "../../components/AreYouSureModal";
import InformationBox from "../../components/InformationBox";
import AddEmployeeEntitlementModal from "../school/AddEmployeeEntitlementModal";

const EntitlementsCardContent = ({ employeeId }) => {
  const { t } = useTranslation("general");

  const [isFetchingEntitlements, setIsFetchingEntitlements] = useState(false);
  const [fetchingEntitlementsError, setFetchingEntitlementsError] =
    useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const tableTitles = [
    { value: "entitlement", keepOnMobile: true },
    { value: "date", keepOnMobile: true },
    { value: "disbursementDate", keepOnMobile: true },
    { value: "amount", keepOnMobile: true },
    { value: "notes", keepOnMobile: true },
  ];

  // Data fetching
  const getAllEntitlementsData = async (page) => {
    try {
      setIsFetchingEntitlements(true);
      setFetchingEntitlementsError(null);
      const response = await axiosClient.get(
        "/userEntitlements/getUserEntitlements",
        {
          params: {
            page,
            userId: employeeId,
            limit: 5,
          },
        }
      );

      const entitlements = response.data.userEntitlements;

      setEntitlements(entitlements);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response?.data?.totalPages,
        totalRecords: response?.data?.totalUserEntitlements,
      });
    } catch (error) {
      console.error(
        "Error fetching entitlements:",
        error.response?.data || error
      );
      setFetchingEntitlementsError("errorFetchingEntitlements");
    } finally {
      setIsFetchingEntitlements(false);
    }
  };

  const tableRows = entitlements.map((employeeEntitlement) => [
    {
      value: employeeEntitlement.entitlement.name,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: employeeEntitlement.date,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: employeeEntitlement.disbursementDate,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: employeeEntitlement.entitlement.price,
      type: TABLE_CELL_TYPES.MONEY,
      keepOnMobile: true,
      currency: "IQD",
    },
    {
      value: employeeEntitlement.notes || t("noNotes"),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
  ]);

  // Effects
  useEffect(() => {
    getAllEntitlementsData(pagination.currentPage);
  }, [pagination.currentPage, employeeId]);

  return (
    <div className="o-column o-column--gap-size_300">
      <div className="o-row o-row--horizontal-align_end o-row--gap-size_500">
        {selectedRows.length > 0 && (
          <CustomButton
            title={t("delete")}
            icon={deleteIcon}
            iconSize={20}
            variant={"red"}
            onClick={() => setShowDeleteModal(true)}
          />
        )}
        <CustomButton
          onClick={() => setShowAddModal(true)}
          title={t("addEntitlement")}
          variant="blank"
          icon={addIcon}
          iconSize={20}
        />
      </div>

      {fetchingEntitlementsError && (
        <ErrorBox>{t(fetchingEntitlementsError)}</ErrorBox>
      )}

      {isFetchingEntitlements && <Loader size={LOADER_SIZES.MEDIUM} />}

      {!fetchingEntitlementsError &&
        !isFetchingEntitlements &&
        entitlements.length === 0 && (
          <InformationBox>{t("noEntitlementsFound")}</InformationBox>
        )}

      {!fetchingEntitlementsError &&
        !isFetchingEntitlements &&
        entitlements.length > 0 && (
          <RoundedTable
            titles={tableTitles}
            rows={tableRows}
            pagination={pagination}
            setPagination={setPagination}
            withCheckbox
            fullData={entitlements}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        )}

      {showDeleteModal && (
        <DeleteModal
          setShowDeleteModal={setShowDeleteModal}
          selectedRows={selectedRows}
          onSuccess={() => {
            setShowDeleteModal(false);
            getAllEntitlementsData(pagination.currentPage);
            setSelectedRows([]);
          }}
        />
      )}

      {showAddModal && (
        <AddEmployeeEntitlementModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            getAllEntitlementsData(pagination.currentPage);
            setSelectedRows([]);
          }}
          employeeId={employeeId}
        />
      )}
    </div>
  );
};

function DeleteModal({ setShowDeleteModal, selectedRows, onSuccess }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingError, setDeletingError] = useState(null);
  const { t } = useTranslation("general");

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await axiosClient.delete(
        "/userEntitlements/deleteMultipleUsersEntitlements",
        {
          data: {
            ids: selectedRows.map((row) => row.id),
          },
        }
      );

      onSuccess();
    } catch (error) {
      console.error(
        "Error deleting entitlements:",
        error.response?.data || error
      );
      setDeletingError("errorDeletingEntitlements");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AreYouSureModal
      onClose={() => setShowDeleteModal(false)}
      onConfirm={handleDelete}
      customMessage={t("areYouSureToDeleteEntitlements")}
      closeTitle="cancel"
      confirmTitle="delete"
      loading={isDeleting}
      confirmButtonVariant="red"
      error={t(deletingError)}
    />
  );
}

export default EntitlementsCardContent;
